/**
 * Left / centre / right, from the child's point of view (CAM-04, MVP notes §5, §17).
 *
 * Two rules carry the behaviour:
 *
 * **Mirroring.** The lane is computed from the mirrored x, so the portal a child walks towards on
 * screen is the portal they choose. `bodyZoneFor` owns the flip.
 *
 * **The first lane is a baseline, not an answer.** A child is always standing somewhere. Reporting
 * the lane they happen to occupy when the detector starts — or when tracking comes back — would
 * arm a portal answer nobody chose, and could commit it after the dwell while the child stood
 * still waiting for the question. The keyboard cannot produce anything of the sort: a lane key is
 * an act. So the first stable lane is adopted silently and only *moves* are reported.
 *
 * The debounce (`zoneChangeDebounceMs`) is what separates a move from a wobble, and the calibrated
 * hysteresis keeps a child standing on a boundary from flickering between two portals.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../../platform/config/tunables';
import type { BodyZone } from '../../../shared/types/input-events';
import type { DetectorOptions, DetectorOutcome, PoseDetector } from '../detector-types';
import { DEFAULT_POSE_CALIBRATION } from '../pose-calibration';
import { bodyZoneFor, torsoCentre } from '../pose-geometry';
import { POSE_LANDMARK } from '../pose-types';
import { createTrackingGate } from './tracking-gate';

const ZONE_LANDMARKS = [
  POSE_LANDMARK.leftShoulder,
  POSE_LANDMARK.rightShoulder,
  POSE_LANDMARK.leftHip,
  POSE_LANDMARK.rightHip,
];

export function createZoneDetector(options: DetectorOptions = {}): PoseDetector {
  const tunables = options.tunables ?? DEFAULT_GAMEPLAY_TUNABLES;
  const calibration = options.calibration ?? DEFAULT_POSE_CALIBRATION;
  const gate = createTrackingGate({ landmarks: ZONE_LANDMARKS, tunables });

  let currentZone: BodyZone | null = null;
  let candidate: BodyZone | null = null;
  let candidateSince = 0;
  /**
   * A lane change was in flight when the mission stopped listening, so the next lane to settle is
   * adopted in silence — **whatever lane it turns out to be**.
   *
   * A child who steps into a portal behind the pause overlay and stays there has not made a movement
   * the mission saw; on the keyboard, holding `KeyA` across Resume emits nothing at all, because a
   * held key produces no second `keydown`. So that occupation is allowed to latch in silence,
   * exactly as the first lane of a sitting does (D-062(2)).
   *
   * **A boolean, not a lane, and that is the whole of round 5's repair (S5-1).** Remembering *which*
   * lane the body was heading for forces a judgement out of data too thin to make it: a candidate is
   * created by one frame, before the debounce has had any say. Keying on the lane meant a single
   * noisy reading behind the overlay could retarget the suppression onto a lane the child never went
   * to, and the lane they were actually walking into then answered a portal; keying on it *without*
   * allowing replacement meant a child who genuinely changed direction mid-pause answered instead.
   * There is no threshold that separates those two honestly. There is no need for one either: the
   * body can only be in one lane when the mission comes back, so the fact worth remembering is that
   * the boundary happened at all.
   *
   * **The invariant this keeps:**
   *
   *  1. the next settle after the boundary is silent, whichever lane it is, and normal reporting
   *     resumes immediately afterwards;
   *  2. it is cancelled the moment the body is observed back in the lane it started from — a
   *     transition the child abandoned is not one anybody has to stay silent about;
   *  3. `currentZone` is retained throughout, so a body that never leaves its lane arms nothing;
   *  4. repeated discards are idempotent by construction — the state is a fact about the boundary,
   *     not about a lane, and noise cannot change a fact about the boundary.
   */
  let awaitingSilentSettle = false;
  /**
   * Whether the body has been read somewhere other than its latched lane since it was last confirmed
   * to be where it is (S6-1).
   *
   * The pause boundary is two calls with an interval between them that the detector is never told
   * about, so "was a lane change in flight across it?" has to be asked of the interval rather than
   * of one instant. Asking only about `candidate` made a single frame decisive in both directions:
   * one noisy reading of another lane retargeted the silence (S5-1), and one noisy reading of the
   * occupied lane stopped it being armed at all (S6-1).
   *
   * A settle clears it, because a settle is the body being confirmed where it is — which is also why
   * this needs no window and no new number: the mark cannot go stale, since staying put for one
   * debounce window *is* a settle.
   */
  let seenElsewhereSinceSettle = false;

  const forget = (): void => {
    currentZone = null;
    candidate = null;
    awaitingSilentSettle = false;
    seenElsewhereSinceSettle = false;
  };

  return {
    capability: 'zone',

    observe(frame): DetectorOutcome {
      const reading = gate.read(frame);
      if (reading.kind === 'blocked') {
        if (reading.lost) forget();
        return reading.outcome;
      }

      const centre = torsoCentre(reading.pose);
      if (centre === null) return { kind: 'none' };

      const zone = bodyZoneFor(centre.x, calibration, currentZone);
      if (zone !== currentZone) seenElsewhereSinceSettle = true;

      /**
       * Every lane goes through the debounce, **including the one the body is already in** (S6-1).
       *
       * The current lane used to take a short-cut out of this function, and that short-cut also
       * cleared the pending silence — so a single noisy frame reading the occupied lane spent it,
       * and the lane the child was actually walking into emitted a movement made behind the pause
       * overlay. A frame is not a settle. Sending every reading down one path is what makes
       * "only a settle spends the silence" true rather than nearly true, and it costs nothing: a
       * lane that settles onto itself reports nothing, because re-settling the lane you are in is
       * not a movement.
       */
      if (zone !== candidate) {
        candidate = zone;
        candidateSince = reading.at;
        return { kind: 'none' };
      }

      if (reading.at - candidateSince < tunables.detector.zoneChangeDebounceMs) {
        return { kind: 'none' };
      }

      const firstEverLane = currentZone === null;
      const movedLane = zone !== currentZone;
      // Clause 1, and now the only way out of the silence: one settle, whichever lane it is. A
      // stationary child's occupation is confirmed by the debounce window passing, which is a settle
      // like any other — so clause 2's abandoned transition is an instance of this rule rather than
      // a second, weaker mechanism beside it.
      const suppressed = awaitingSilentSettle;
      awaitingSilentSettle = false;
      currentZone = zone;
      candidate = null;
      // Settled: wherever the body is, it is confirmed to be there.
      seenElsewhereSinceSettle = false;

      // Standing where you already stood is not a movement; nor is the first lane of a sitting, nor
      // the one the pause left the child in.
      if (!movedLane || firstEverLane || suppressed) return { kind: 'none' };

      return {
        kind: 'event',
        event: { type: 'PLAYER_ZONE_CHANGED', zone },
        confidence: reading.confidence,
      };
    },

    /**
     * The lane change part-way through its debounce is discarded, and the boundary is remembered —
     * see `awaitingSilentSettle` for the whole invariant.
     *
     * `currentZone` is deliberately kept: the child has been watched the whole time the mission was
     * held, so where they are standing is not in doubt, and forgetting it would make the first
     * 200 ms after Resume eat the step they take to answer.
     *
     * Nothing in flight means nothing happened worth being silent about — a child standing still
     * when the grown-up presses Pause answers the very next step normally. Both edges of the pause
     * boundary call this, often with no camera frame in between, and a noisy frame between them
     * cannot change anything: the state records that a boundary was crossed mid-movement, not which
     * lane the movement was toward.
     */
    discardInProgress(): void {
      /**
       * Was a lane change in flight across this boundary? Asked of the recent past, not of this
       * instant (S6-1).
       *
       * Every reading now goes through the candidate slot, the current lane included, so a child
       * standing perfectly still has a candidate too — and one noisy frame is enough to make either
       * answer wrong if `candidate` is all you look at. So the question is whether the body has been
       * read anywhere else since it was last confirmed where it is. If it has, a transition
       * straddling this boundary is still possible, and the ambiguity is resolved by staying silent:
       * eating a step costs a child one repetition, inventing one answers a question they never saw.
       */
      if (!seenElsewhereSinceSettle) return;

      awaitingSilentSettle = true;
      // Only a transition is thrown away. A candidate for the lane the body is standing in is the
      // debounce confirming that it is standing still, and restarting that clock at every boundary
      // would stop it ever being confirmed.
      if (candidate !== null && candidate !== currentZone) candidate = null;
    },

    /**
     * The interval before this frame was not observed — and **nothing here is invalidated by that**.
     *
     * Arrived at over three attempts, two of which took a real answer away from a child and are
     * recorded rather than quietly replaced.
     *
     * What an unobserved interval hides is a *posture*: hands can go up and come down inside one and
     * the returning frame cannot tell, which is why every posture detector re-baselines. **A lane is
     * not a posture.** It is a place, and this detector never reports one until the body has held it
     * for the debounce — a dwell that happens *after* the hole, with frames flowing and the child
     * plainly standing in the portal. Anything done to that is either unnecessary (the dwell
     * re-establishes the fact) or harmful (it deletes an answer the child is visibly giving).
     *
     * The three attempts, in order:
     *
     *  - `reset()` — forgetting the lane — turned the child's next step into the silently-adopted
     *    "first ever" lane and ate a portal answer after every mid-mission camera restart.
     *  - discarding an in-flight transition, the pause boundary's rule (D-062(2)) — ate a portal
     *    answer under parallel load, where the driver's own pauses between frames are long enough to
     *    look like holes and the body has been "seen elsewhere" since it last settled.
     *  - resetting the loss clock and nothing else (round 4) — harmless, but not this method's to
     *    do: S5-3 established that how long since a body could be seen is exactly the fact an
     *    unobserved interval must not refresh, or a child who left before the hole is never reported
     *    gone. Removed rather than kept as decoration.
     *
     * …and then a fourth, which is the principle taken at its own words rather than approximately
     * (S6-5). "Only claims about watched time" is a test this detector holds **two** answers to, not
     * one, and the fourth attempt is the first to separate them:
     *
     *  - `currentZone` is a claim re-verified by the present frame — the body is standing in that
     *    portal in the picture that came back — so it survives, which is the whole of the first
     *    three attempts' hard-won lesson;
     *  - `candidateSince` is a **stopwatch**. "This body has been settling into a new lane since t"
     *    is exactly a claim about watched time, and an interval nobody watched is not time this body
     *    spent anywhere. Counting it turned one pre-hole reading and one post-hole reading into a
     *    settled lane change — two frames half a second apart committing a portal answer, under a
     *    650 ms dwell and a 2 000 ms starvation clock that never notice.
     *
     * So the stopwatch restarts and nothing else moves. Deliberately **not** the pause boundary's
     * rule: nothing is suppressed, no silence is armed, and the child's next settle reports normally
     * — it simply has to be one this detector watched happen.
     *
     * The exposure this accepts, unchanged and now precise: a lane change that *began* before the
     * hole is still reported, once the body has held the new lane for a debounce **after** it. That
     * report is a dwell nobody has to take on trust; the pause boundary keeps its own stricter rule,
     * because a pause is a state the mission entered deliberately and a keyboard's `keydown` really
     * was consumed by it, and a hole in the frames is neither.
     */
    reacquireSilently(): void {
      candidate = null;
    },

    reset(): void {
      gate.reset();
      forget();
    },
  };
}
